import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MonthPicker } from "@/components/husholdning/MonthPicker";
import { EntryDialog } from "@/components/husholdning/EntryDialog";
import { ChoosePlanState } from "@/components/husholdning/ChoosePlanState";
import { CategoryPicker, CategoryChip } from "@/components/husholdning/CategoryPicker";
import { formatKr, formatDate, formatMonth } from "@/lib/format";
import { currentMonth, isValidMonth, HOUSEHOLD_SOURCES, HOUSEHOLD_KINDS, HOUSEHOLD_HOME, planTerms } from "@/lib/householdConstants";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Search, X, Download, Plus, ExternalLink, Pencil, Trash2, AlertTriangle, ArrowLeftRight, ChevronLeft, ChevronRight, Loader2, Clock } from "lucide-react";

const PAGE_SIZE = 50;

const SourceBadge = ({ source }) => {
  const s = HOUSEHOLD_SOURCES[source] || { label: source, cls: "status-avvist" };
  const Icon = s.icon;
  return <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", s.cls)} data-testid={`household-tx-source-${source}`}>{Icon ? <Icon className="h-3 w-3" /> : null}{s.short}</span>;
};

/** Brikke for fakturaer som ikke er tatt med i summene ennå. */
const PendingChip = ({ testId }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[11px] font-medium whitespace-nowrap status-forfaller_snart" data-testid={testId}><Clock className="h-3 w-3" /> Venter på kategori</span>
);

const Amount = ({ item, className }) => (
  <span className={cn("font-medium tnum whitespace-nowrap", item.kind === "inntekt" ? "text-success" : "", item.amount < 0 && "text-flag", item.included === false && "text-muted-foreground line-through decoration-muted-foreground/60", className)} title={item.included === false ? "Ikke tatt med i summene før kategori er valgt" : undefined}>
    {item.kind === "inntekt" ? (item.amount < 0 ? "−" : "+") : "−"}{formatKr(Math.abs(item.amount))}
  </span>
);

/** Regnskap → Transaksjoner: alle poster (regninger, fakturaer, manuelle) for en måned, med filtre og kategorisering. */
export default function HouseholdTransactionsPage() {
  const { canWrite, isOwner } = useAuth();
  const [params, setParams] = useSearchParams();
  const month = isValidMonth(params.get("mnd")) ? params.get("mnd") : currentMonth();
  const kind = params.get("type") || "alle";
  const source = params.get("kilde") || "alle";
  const category = params.get("kategori") || "";
  const q = params.get("q") || "";
  const page = Math.max(1, Number(params.get("side") || 1));
  const wantNew = params.get("ny") === "1";
  const focusPost = params.get("post");

  const [search, setSearch] = useState(q);
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const planRef = useRef(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [entryDialog, setEntryDialog] = useState(null); // { entry } | { defaults }
  const [del, setDel] = useState(null);
  const [assigning, setAssigning] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => { if (wantNew && canWrite) { setEntryDialog({ defaults: {} }); const p = new URLSearchParams(params); p.delete("ny"); setParams(p, { replace: true }); } }, [wantNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setError("");
    const p = { month, page, limit: PAGE_SIZE, kind: kind !== "alle" ? kind : undefined, source: source !== "alle" ? source : undefined, category_id: category || undefined, q: q || undefined };
    const cached = planRef.current && !planRef.current.needs_plan ? planRef.current : null;
    Promise.all([api.get("/household/transactions", { params: p }), cached ? Promise.resolve({ data: cached }) : api.get("/household/plan")])
      .then(([t, pl]) => { setData(t.data); if (!cached) { planRef.current = pl.data; setPlan(pl.data); } })
      .catch((e) => setError(errorMessage(e)));
  }, [month, page, kind, source, category, q]);
  useEffect(() => { load(); }, [load]);

  // Åpne en bestemt manuell post (lenke fra oversikt)
  useEffect(() => {
    if (!focusPost || !data) return;
    const it = data.items.find((i) => i.source === "manuell" && i.source_id === focusPost);
    if (it) setSelected(it);
  }, [focusPost, data]);

  const setParam = (patch) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) p.set(k, v); else p.delete(k); }
    if (!("side" in patch)) p.delete("side");
    setParams(p, { replace: true });
  };
  const hasFilters = !!(q || category || kind !== "alle" || source !== "alle");
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const categoryLabel = useMemo(() => {
    if (!category || !plan) return null;
    if (category === "ingen") return "Ukategorisert";
    if (category === "venter") return "Venter på kategori";
    for (const g of plan.groups) { if (g.id === category) return `${g.code} ${g.name}`; const c = g.children.find((x) => x.id === category); if (c) return `${c.code} ${c.name}`; }
    const i = plan.income.find((x) => x.id === category);
    return i ? `${i.code} ${i.name}` : null;
  }, [category, plan]);
  const t = planTerms(plan?.standard);

  const assign = async (item, categoryId) => {
    setAssigning(item.id);
    try {
      const r = await api.put("/household/assign", { source: item.source, id: item.source_id, category_id: categoryId });
      toast.success(categoryId ? `Kategori satt til ${r.data.category_code} ${r.data.category_name}` : item.source === "faktura" ? "Kategori fjernet – fakturaen venter på kategori" : "Overstyring fjernet – bruker koblingen igjen");
      await load();
      setSelected((s) => (s && s.id === item.id ? { ...s, category_id: r.data.category_id, category_code: r.data.category_code, category_name: r.data.category_name, overridden: !!categoryId, included: item.source === "faktura" ? !!categoryId : true } : s));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setAssigning("");
    }
  };

  const removeEntry = async () => {
    const it = del;
    setDel(null);
    try {
      await api.delete(`/household/entries/${it.source_id}`);
      toast.success("Posten er slettet");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadFile("/household/export", `regnskap_${month}.csv`, { month }); toast.success("CSV lastet ned"); } catch (e) { toast.error(errorMessage(e)); } finally { setExporting(false); }
  };

  const toEntry = (item) => ({ id: item.source_id, kind: item.kind, date: item.date, amount: Math.abs(item.amount), category_id: item.category_id, description: item.title, counterparty: item.counterparty, note: item.note });

  if (data?.needs_plan) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Regnskap" title="Transaksjoner" description="Alle poster i regnskapet: regninger, utstedte fakturaer og manuelle poster." />
        <ChoosePlanState data={data} onChosen={() => { planRef.current = null; setPlan(null); setData(null); load(); }} />
      </div>
    );
  }

  /** Kategori-celle: ukategoriserte regninger og fakturaer som venter får nedtrekk direkte i raden (brukerbeslutning). */
  const CategoryCell = ({ it, compact }) => {
    const needsPick = it.source !== "manuell" && !it.category_id;
    if (needsPick && plan && canWrite) {
      return (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className={cn("min-w-[200px]", compact ? "w-full" : "max-w-[300px]")} data-testid={`household-tx-inline-${it.source}-${it.source_id}`}>
          <CategoryPicker plan={plan} kind={it.kind} value={null} onChange={(v) => v && assign(it, v)} disabled={assigning === it.id} allowNone={false}
            placeholder={it.source === "faktura" ? `Venter – velg ${t.income}` : "Ukategorisert – velg kategori"} className={cn("h-8 text-xs", it.source === "faktura" ? "border-dashed status-forfaller_snart" : "border-dashed")} testId={`household-tx-inline-picker-${it.source}-${it.source_id}`} />
        </div>
      );
    }
    if (it.included === false) return <PendingChip testId={`household-tx-cat-${it.source}-${it.source_id}`} />;
    if (it.category_id) return <><CategoryChip code={it.category_code} name={it.category_name} color={it.group_color} testId={`household-tx-cat-${it.source}-${it.source_id}`} />{it.overridden && it.source === "regning" ? <span className="ml-1.5 text-[11px] text-muted-foreground">overstyrt</span> : null}</>;
    return <CategoryChip name="Ukategorisert" muted testId={`household-tx-cat-${it.source}-${it.source_id}`} />;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t.eyebrow}
        title="Transaksjoner"
        description={`Alle poster i ${t.regnskapOrd}: regninger (utgift i forfallsmåneden), utstedte fakturaer (inntekt i fakturamåneden når ${t.income} er valgt) og manuelle poster.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MonthPicker month={month} onChange={(m) => setParam({ mnd: m === currentMonth() ? "" : m })} />
            <Button variant="outline" onClick={exportCsv} disabled={exporting || !data || data.total === 0} data-testid="household-tx-export-button">{exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} CSV</Button>
            {canWrite ? <Button onClick={() => setEntryDialog({ defaults: { date: month === currentMonth() ? undefined : `${month}-01` } })} data-testid="household-tx-add-button"><Plus className="h-4 w-4 mr-2" /> Ny post</Button> : null}
          </div>
        }
      />

      {/* Filtre */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <Tabs value={kind} onValueChange={(v) => setParam({ type: v === "alle" ? "" : v })}>
          <TabsList data-testid="household-tx-kind-tabs">
            <TabsTrigger value="alle" data-testid="household-tx-kind-alle">Alle</TabsTrigger>
            <TabsTrigger value="utgift" data-testid="household-tx-kind-utgift">Utgifter</TabsTrigger>
            <TabsTrigger value="inntekt" data-testid="household-tx-kind-inntekt">Inntekter</TabsTrigger>
          </TabsList>
        </Tabs>
        <form onSubmit={(e) => { e.preventDefault(); setParam({ q: search.trim() }); }} className="relative w-full lg:w-72" role="search">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk leverandør, kunde, tekst…" className="pl-9 pr-9" data-testid="household-tx-search" aria-label="Søk i transaksjoner" />
          {search ? <button type="button" onClick={() => { setSearch(""); setParam({ q: "" }); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Tøm søk"><X className="h-4 w-4" /></button> : null}
        </form>
        <Select value={source} onValueChange={(v) => setParam({ kilde: v === "alle" ? "" : v })}>
          <SelectTrigger className="h-10 w-full lg:w-44" data-testid="household-tx-source-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card">
            <SelectItem value="alle">Alle kilder</SelectItem>
            {Object.entries(HOUSEHOLD_SOURCES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {plan ? (
          <div className="w-full lg:w-72">
            <CategoryPicker plan={plan} kind={kind === "inntekt" ? "inntekt" : "utgift"} value={category && category !== "ingen" && category !== "venter" ? category : null} onChange={(v) => setParam({ kategori: v || "" })} noneLabel="Alle kategorier" placeholder="Alle kategorier" testId="household-tx-category-filter" />
          </div>
        ) : null}
        {data?.pending?.count > 0 || category === "venter" ? (
          <Button variant={category === "venter" ? "secondary" : "outline"} size="sm" className="h-10" onClick={() => setParam({ kategori: category === "venter" ? "" : "venter" })} data-testid="household-tx-pending-filter"><Clock className="h-4 w-4 mr-1.5" /> Venter på kategori{data?.pending?.count && category !== "venter" ? ` (${data.pending.count})` : ""}</Button>
        ) : null}
        {hasFilters ? <Button variant="ghost" size="sm" className="h-10" onClick={() => setParams(month === currentMonth() ? {} : { mnd: month }, { replace: true })} data-testid="household-tx-reset"><X className="h-4 w-4 mr-1" /> Nullstill</Button> : null}
      </div>
      {category === "ingen" ? <p className="text-sm text-muted-foreground -mt-2" data-testid="household-tx-filter-none">Viser ukategoriserte poster (telles i totalen, men ikke i {t.group}ne). <button type="button" className="underline" onClick={() => setParam({ kategori: "" })}>Vis alle</button></p>
        : category === "venter" ? <p className="text-sm text-muted-foreground -mt-2" data-testid="household-tx-filter-pending">Fakturaer som venter på {t.income} – de telles ikke i summene før du velger kategori. <button type="button" className="underline" onClick={() => setParam({ kategori: "" })}>Vis alle</button></p>
          : categoryLabel ? <p className="text-sm text-muted-foreground -mt-2">Kategori: <strong>{categoryLabel}</strong></p> : null}

      {error ? <EmptyState icon={AlertTriangle} title="Kunne ikke laste transaksjoner" description={error} actionLabel="Prøv igjen" onAction={load} testId="household-tx-error" />
        : !data ? <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}</div>
          : data.items.length === 0 ? (
            <EmptyState icon={ArrowLeftRight} title={hasFilters ? "Ingen treff" : `Ingen poster i ${formatMonth(month).toLowerCase()}`} description={hasFilters ? "Prøv et annet filter eller nullstill." : "Regninger og fakturaer kommer automatisk. Legg til lønn og andre poster manuelt."} actionLabel={hasFilters ? "Nullstill filter" : canWrite ? "Ny post" : undefined} onAction={hasFilters ? () => setParams(month === currentMonth() ? {} : { mnd: month }, { replace: true }) : canWrite ? () => setEntryDialog({ defaults: {} }) : undefined} testId="household-tx-empty" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="household-tx-sums">
                <span className="text-muted-foreground">{data.total} {data.total === 1 ? "post" : "poster"}</span>
                <span>Inntekter: <strong className="tnum text-success" data-testid="household-tx-sum-income">{formatKr(data.sum_income)}</strong></span>
                <span>{t.expenses}: <strong className="tnum" data-testid="household-tx-sum-expense">{formatKr(data.sum_expense)}</strong></span>
                <span>Resultat: <strong className={cn("tnum", data.sum_income - data.sum_expense < 0 && "text-flag")} data-testid="household-tx-sum-result">{formatKr(data.sum_income - data.sum_expense)}</strong></span>
                {data.pending?.count > 0 ? <span className="text-muted-foreground" data-testid="household-tx-sum-pending">Venter på kategori: {data.pending.count} ({formatKr(data.pending.total)}) – ikke tatt med</span> : null}
              </div>
              <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
                <Table data-testid="household-tx-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[110px]">Dato</TableHead>
                      <TableHead>Beskrivelse</TableHead>
                      <TableHead className="w-[110px]">Kilde</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right w-[140px]">Beløp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((it) => (
                      <TableRow key={it.id} className="cursor-pointer" onClick={() => setSelected(it)} data-testid={`household-tx-row-${it.source}-${it.source_id}`}>
                        <TableCell className="tnum text-muted-foreground">{formatDate(it.date)}</TableCell>
                        <TableCell><p className="font-medium truncate max-w-[320px]">{it.title}</p><p className="text-xs text-muted-foreground truncate max-w-[320px]">{it.subtitle}</p></TableCell>
                        <TableCell><SourceBadge source={it.source} /></TableCell>
                        <TableCell><CategoryCell it={it} /></TableCell>
                        <TableCell className="text-right"><Amount item={it} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ul className="md:hidden space-y-2" data-testid="household-tx-cards">
                {data.items.map((it) => (
                  <li key={it.id}>
                    <div role="button" tabIndex={0} onClick={() => setSelected(it)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(it); } }} className="w-full text-left rounded-xl border bg-card p-4 card-lift cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`household-tx-card-${it.source}-${it.source_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0"><p className="font-medium text-sm truncate">{it.title}</p><p className="text-xs text-muted-foreground truncate">{it.subtitle} · {formatDate(it.date)}</p></div>
                        <Amount item={it} className="text-sm" />
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap"><SourceBadge source={it.source} /><CategoryCell it={it} compact /></div>
                    </div>
                  </li>
                ))}
              </ul>
              {totalPages > 1 ? (
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam({ side: String(page - 1) })} data-testid="household-tx-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Forrige</Button>
                  <span className="text-xs text-muted-foreground">Side {page} av {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setParam({ side: String(page + 1) })} data-testid="household-tx-next">Neste <ChevronRight className="h-4 w-4 ml-1" /></Button>
                </div>
              ) : null}
            </>
          )}

      {/* Detalj */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-card w-full sm:max-w-md overflow-y-auto" data-testid="household-tx-detail">
          {selected ? (
            <>
              <SheetHeader className="text-left">
                <div className="flex items-center gap-2 pr-6 flex-wrap"><SourceBadge source={selected.source} /><span className={cn("text-xs font-medium", HOUSEHOLD_KINDS[selected.kind]?.tone)}>{HOUSEHOLD_KINDS[selected.kind]?.label}</span></div>
                <SheetTitle className="break-words">{selected.title}</SheetTitle>
                <SheetDescription>{selected.subtitle} · {formatDate(selected.date)}</SheetDescription>
              </SheetHeader>
              <p className="mt-4 font-heading text-3xl font-semibold tnum" data-testid="household-tx-detail-amount"><Amount item={selected} /></p>
              {selected.included === false ? <p className="mt-2 text-sm status-forfaller_snart border rounded-lg px-3 py-2 flex items-start gap-2" data-testid="household-tx-detail-pending"><Clock className="h-4 w-4 mt-0.5 shrink-0" /> Venter på {t.income}. Fakturaen telles ikke i {t.regnskapOrd} før du har valgt kategori under.</p> : null}
              {selected.note ? <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">{selected.note}</p> : null}
              {selected.created_by ? <p className="mt-1 text-xs text-muted-foreground">Ført av {selected.created_by}</p> : null}

              <div className="mt-6 space-y-2">
                <p className="text-sm font-medium">{selected.kind === "inntekt" ? planTerms(plan?.standard).income.replace(/^./, (c) => c.toUpperCase()) : "Kategori"}</p>
                {plan && canWrite ? (
                  <>
                    <CategoryPicker plan={plan} kind={selected.kind} value={selected.category_id} onChange={(v) => assign(selected, v)} disabled={assigning === selected.id} noneLabel={selected.source === "manuell" ? "Uten kategori" : selected.source === "faktura" ? "Ikke valgt (venter på kategori)" : "Standard (fra Regninger-kategorien)"} testId="household-tx-detail-category" />
                    <p className="text-xs text-muted-foreground">{selected.source === "regning" ? "Valget overstyrer koblingen fra Regninger-kategorien for denne regningen." : selected.source === "faktura" ? "Fakturaen telles som inntekt i fakturamåneden så snart kategori er valgt." : "Kategorien lagres på posten."}</p>
                  </>
                ) : selected.included === false ? (
                  <PendingChip />
                ) : (
                  <CategoryChip code={selected.category_code} name={selected.category_name || "Ukategorisert"} color={selected.group_color} muted={!selected.category_id} />
                )}
              </div>

              <div className="mt-6 flex flex-col gap-2">
                {selected.source !== "manuell" ? (
                  <Button asChild variant="outline" data-testid="household-tx-detail-open"><Link to={selected.link}><ExternalLink className="h-4 w-4 mr-2" /> Åpne i {selected.source === "regning" ? "Regninger" : "Faktura"}</Link></Button>
                ) : (
                  <>
                    {canWrite ? <Button variant="outline" onClick={() => setEntryDialog({ entry: toEntry(selected) })} data-testid="household-tx-detail-edit"><Pencil className="h-4 w-4 mr-2" /> Rediger post</Button> : null}
                    {isOwner ? <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDel(selected)} data-testid="household-tx-detail-delete"><Trash2 className="h-4 w-4 mr-2" /> Slett post</Button> : null}
                  </>
                )}
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <EntryDialog open={!!entryDialog} onOpenChange={(o) => !o && setEntryDialog(null)} plan={plan} entry={entryDialog?.entry || null} defaults={entryDialog?.defaults} onSaved={() => { load(); setSelected(null); }} />

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-card" data-testid="household-tx-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Slette posten?</AlertDialogTitle><AlertDialogDescription>«{del?.title}» ({formatKr(Math.abs(del?.amount || 0))}) fjernes fra {t.regnskapOrd}. Dette kan ikke angres.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="household-tx-delete-cancel">Avbryt</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={removeEntry} data-testid="household-tx-delete-confirm">Slett</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
